import * as THREE from "three";
import { groundAt } from "../terrain";
import { GroundMesh } from "../groundMesh";
import { Enemy, EnemyAct, SimConstants, BalanceData } from "@highmarch/domain";
import { FLYER_HEIGHT, HQ_YAW, ROAD_Y, roadY } from "./common";
import { RING, UnitRings, hoverShrink } from "./blobShadow";
import { TEAM_RIM, footfallSquash, springStep } from "../unitFx";
import type { VFX } from "../../../../../art/vfx.js";
import { StatusBit, gateStep, hasMarks, statusGate, statusMarks } from "../statusFx";
import { BossStage } from "./bossStage";
import { IMMUNE_STATUS, STATUS_COLOUR, affixStatus, colourBlind, enemyLook, inferRole, roleColour } from "./enemyLook";
import { immunityKinds, immunityOf } from "../../rare/rareApi";
import { ROLE_CELL, STATUS_CELL } from "./enemyGlyphs";
import { EnemyMarks } from "./enemyMarks";
import { WYRM_CLIPS, flyHeight } from "./wyrmView";
import { playStrike } from "./bossClips";
import { ENEMY_GAIT, fallbackRate, footLock, runShare, walkCycles } from "./enemyGait";
import { roundPath, turnBody } from "./enemyTurn";
import { type Footing, followGround, footing } from "./groundFollow";
import { deckSagAt } from "./road";
import { FOOT_IK } from "./footIk";
import { AURA_ALPHA, BADGE_PX, EMERGE_DEPTH, EMERGE_SECONDS, EnemyView, FEEL, FLOAT, MARK_RING, PLATE_LIFT, RING_BOOST, RING_BURN, RING_ENEMY, RING_ICE, RING_POISON, STATUS_ORDER, emergeScale } from "./enemyView";

/** An enemy view's feel (enemies.ts EnemyViews runs it): its health shown, hit, footfalls and lean, ground ring, status marks and role wash. */

const _ringCol = new THREE.Color();
/** A boss's rune ring colour: the boss team's ember (unitFx.ts TEAM_RIM), whatever its own rim. */
const BOSS_RING = new THREE.Color(TEAM_RIM.boss.color);
/** A buried Burrower (ENEMIES_V2): sunk its height and this much more, the plate lowered by this share, the ring faded by this share. */
const BURY_EXTRA = 0.08, BURY_PLATE = 0.75, BURY_RING = 0.8;
const _boss = { rise: 0, scale: 1 };

/**
 * The health a unit shows: the sim's, plus the hits whose rounds are still in the air. The `hit` clip plays only
 * for a heavy drop (≥ 8 % of max hp) so a unit under a tesla or archer stream stops flinching permanently (units F5).
 */
export function showHp(v: EnemyView, simHp: number): void {
  const hp = Math.min(v.maxHp, simHp + v.owed);
  if (v.lastHp - hp >= FEEL.hitClipFraction * v.maxHp && !v.rig.busy) playStrike(v, "hit");
  v.lastHp = hp;
  v.bar.set(hp / v.maxHp);
  // the plate's bar: hidden at full health, and for a boss (its health is on the DOM boss bar)
  v.hp = v.boss || hp >= v.maxHp * 0.999 ? -1 : Math.max(0, Math.min(1, hp / v.maxHp));
}

/** A hit's feel: flash, a 60 ms stretch and a 5 cm shove away from the tower at (tx, tz) (NaN: no tower). */
export function hitFeel(v: EnemyView, tx: number, tz: number, reduced: boolean): void {
  v.look?.flash((reduced ? 0.5 : 1) * (v.wyrm ? WYRM_CLIPS.hitFlash : 1));
  v.stretch = FEEL.stretchFor;
  if (Number.isNaN(tx) || v.boss) return;
  let dx = v.cur.x - tx, dz = v.cur.z - tz;
  const d = Math.hypot(dx, dz);
  if (d < 1e-4) return;
  dx /= d; dz /= d;
  // add the new shove to what is left of the last one, capped, along the newest direction
  const along = Math.min(FEEL.knockMax, Math.max(0, v.knock.x) + FEEL.knock);
  v.kdx = dx; v.kdz = dz; v.knock.x = along;
}

/** Squash on footfalls, lean with speed, knockback spring, hit stretch and spawn pop — all on the body node. */
export function feel(v: EnemyView, dt: number, moving: boolean, stage: BossStage, reduced: boolean): void {
  springStep(v.knock, FEEL.springOmega, dt);
  v.body.position.x = v.kdx * v.knock.x;
  v.body.position.z = v.kdz * v.knock.x;
  if (v.knock.x > KNOCK_RESAMPLE) knockGround(v);
  // a boss without a planted walk (the Necromancer floats, the Harpy flies) has no footfalls: no squash, no dust
  const walking = moving && v.rig.loopClip === "walk" && (v.stride > 0 || !v.bs);
  let k = walking ? footfallSquash(v.rig.loopPhase()) : 0;
  // a planted walk (a measured stride) is flattened but not widened: widening scales the body about its root, which
  // slid the planted soles a body-length apart by a few mm every footfall
  let wide = v.stride > 0 ? 0 : 0.5 * k;
  if (v.stretch > 0) { v.stretch -= dt; k = FEEL.stretch; wide = 0.5 * k; }
  let grow = emergeScale(v.age);
  if (!v.bs && v.age < EMERGE_SECONDS) { const t = v.age / EMERGE_SECONDS; v.body.position.y -= EMERGE_DEPTH * (1 - t) * (1 - t); }
  if (v.bs) {
    stage.frame(v.bs, dt, v.bodyH * v.scale, walking ? v.rig.loopPhase() : NaN, reduced, _boss);
    grow = _boss.scale;
    v.body.position.y += _boss.rise;
  }
  // a Burrower's dive (ENEMIES_V2): the body sinks below the road and the plate settles onto its mound
  if (v.buried > 0 && !v.rig.has("burrow")) v.body.position.y -= v.buried * (v.headY + BURY_EXTRA);   // a model with its own `burrow` clip sinks itself
  const s = v.scale * grow;
  v.headNow = (v.headY * grow + (v.bs ? _boss.rise : 0)) * (1 - BURY_PLATE * v.buried);   // the plate rides on the head as the unit climbs or grows
  v.body.scale.set(s * (1 + wide), s * (1 - k), s * (1 + wide));
  // the model faces −z before HQ_YAW, so leaning into the stride is a negative turn about its own x (order YXZ); a unit
  // whose walk cannot keep up with it (enemyGait.ts runShare) leans further and bobs off each footfall: it runs
  const run = walking && !v.bs ? v.run : 0;
  if (run > 0) v.body.position.y += ENEMY_GAIT.run.bob * s * run * 0.5 * (1 - Math.cos(4 * Math.PI * v.rig.loopPhase()));
  v.body.rotation.x = -(FEEL.lean * Math.min(1, v.speed / FEEL.leanAtSpeed) + ENEMY_GAIT.run.lean * run) * (moving ? 1 : 0) + v.foot.pitch;
  v.body.rotation.z = -v.foot.roll;                     // its own +x is its right: a left side up is a negative turn
  // the soles on the ground under each (footIk.ts) — not while it climbs out of the ground, burrows or flies
  const staged = v.age < EMERGE_SECONDS || v.buried > 0 || (v.bs !== null && _boss.rise !== 0);
  if (v.ik && !v.air && !staged) v.ik.solve(v.body, groundUnder, v.foot.relief > FOOT_IK.unevenAbove, dt);
}

/** A shove shorter than this (world units) is not worth a ground sample. */
const KNOCK_RESAMPLE = 1e-3;
/** A shoved body stands on the ground where the shove put it, not at the height of the spot it was shoved from. */
function knockGround(v: EnemyView): void {
  const p = v.root.position;
  v.body.position.y += groundUnder(p.x + v.body.position.x, p.z + v.body.position.z) - groundUnder(p.x, p.z);
}

/**
 * The ground ring under a unit. An ordinary enemy's is the marks layer's ring in its role colour (enemyLook.ts); the
 * boss keeps its wide rune ring (UnitRings), pulled toward the strongest status (ice, poison pulse, burn, boost).
 */
export function pushRing(v: EnemyView, hover: number, fade: number, marks: EnemyMarks, rings: UnitRings, stage: BossStage, time: number): void {
  const p = v.root.position;
  if (!v.boss) {
    const big = v.role.role === "brute" ? "big" : "unit";
    marks.ring(p.x + v.body.position.x, p.y + roadY() + 0.004, p.z + v.body.position.z, MARK_RING[big] * (v.air ? 0.8 : 1) * (hover > 0 ? hoverShrink(hover) : 1), v.colour, fade * (1 - BURY_RING * v.buried), v.air);
    return;
  }
  const st = v.look?.status;
  // the rune ring keeps the boss ember (a boss's rim is its own colour now); a rare's and the Wyrm's follow their shine
  _ringCol.copy(v.rare > 0 || v.wyrm ? v.look?.rimColor ?? RING_ENEMY : BOSS_RING);
  if (st) {
    if (st.x > 0.01) _ringCol.lerp(RING_ICE, st.x * 0.85);
    if (st.z > 0.01) _ringCol.lerp(RING_POISON, st.z * (0.45 + 0.35 * Math.sin(time * Math.PI * 4)));
    if (st.y > 0.01) _ringCol.lerp(RING_BURN, st.y * 0.6);
    if (st.w > 0.01) _ringCol.lerp(RING_BOOST, st.w * 0.7);
  }
  const width = v.ringWidth * (hover > 0 ? hoverShrink(hover) : 1);
  const f = v.bs && v.dead <= 0 ? fade * stage.ringFade(v.bs) : fade;
  rings.push(p.x + v.body.position.x, p.y, p.z + v.body.position.z, width, _ringCol, RING.rimAlpha, RING.shadowAlpha, v.boss, f, v.look?.heat ?? 0);
}

/**
 * Status from the sim (slow, burn, poison = shred stacks, boost) as shader targets — `UnitLook.update` eases them —
 * and as the marks drawn on the unit. Slow, burn and shred wait for the round that brings them to land (statusFx.ts
 * StatusGate): the sim applies them the tick the tower fires, while the shard is still on the crystal.
 */
export function readStatus(e: Enemy, v: EnemyView, gated: boolean, B: BalanceData): void {
  const m = statusMarks(e, v.marks);
  // no rounds fly without the sprite VFX (effects.ts routes nothing): nothing to wait for
  if (gated) {
    if (!gateStep(statusGate, e.id, StatusBit.Frost, m.frost > 0, v.waits, 0)) { m.frost = 0; m.frozen = false; }
    if (!gateStep(statusGate, e.id, StatusBit.Burn, m.burn > 0, v.waits, 1)) m.burn = 0;
    if (!gateStep(statusGate, e.id, StatusBit.Shred, m.shred > 0, v.waits, 2)) m.shred = 0;
  }
  const t = v.look?.target;
  if (t) t.set(m.frost > 0 ? 1 : 0, m.burn > 0 ? 1 : 0, m.shred > 0 ? Math.min(1, 0.45 + 0.11 * m.shred) : 0, e.boostTicksLeft > 0 ? 1 : 0);
  // the plate's two status icons, in priority order; an elite's affix (enemyLook.ts) holds the first for good
  const affix = affixStatus(B, e);
  v.s1 = affix ? STATUS_CELL[affix] : -1; v.s2 = -1;
  // M36: an immune unit's immunity holds the first icon for good (it is never also an elite), the aura shows it too
  const imm = immunityOf(e);
  v.imm = imm;
  if (imm !== 0) {
    const kinds = immunityKinds(imm);
    v.s1 = STATUS_CELL[IMMUNE_STATUS[kinds[0]]];
    if (kinds.length > 1) { v.s2 = STATUS_CELL[IMMUNE_STATUS[kinds[1]]]; return; }
  }
  for (const s of STATUS_ORDER) {
    const on = s === "burning" ? m.burn > 0 : s === "slowed" ? m.frost > 0 : s === "shred" ? m.shred > 0
      : s === "boosted" ? e.boostTicksLeft > 0 : s === "shielded" ? e.shieldArmour.isPositive()
      : s === "burrowed" ? e.burrowed : s === "casting" && (e.act === EnemyAct.Summoning || e.act === EnemyAct.WindingUp);
    if (!on) continue;
    if (v.s1 < 0) v.s1 = STATUS_CELL[s];
    else { v.s2 = STATUS_CELL[s]; return; }
  }
}

/**
 * Sets a type's emissive wash (a uniform: no new program): none today for a regular enemy or a boss (enemyLook.ts WASH,
 * WASH_BOSS: it hid the art), kept as the one place a role tint would go. The wash follows the archetype's own role even for a boss-flagged spawn. The view's materials are
 * its UnitLook clones, so the wash is per view; `washed` still skips a material already at this palette.
 */
export function wash(body: THREE.Object3D, arch: Parameters<typeof inferRole>[0]): void {
  const role = inferRole(arch);
  const cb = colourBlind();
  const strength = EnemyWash.on ? enemyLook(arch).wash : 0;
  const tag = `${role}|${cb}|${strength}`;
  body.traverse((o) => {
    const m = (o as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
    if (!m || !(o as THREE.Mesh).isMesh || !(m as THREE.MeshStandardMaterial).isMeshStandardMaterial || washed.get(m) === tag) return;
    washed.set(m, tag);
    m.emissive.setHex(roleColour(role, cb)).multiplyScalar(strength);
  });
}

/**
 * The wash is a play cue, like the HP bars and role badges: a staged scene that is art, not play (the title's diorama,
 * render/titleDiorama.ts), turns it off for the views it builds while it stands.
 */
export const EnemyWash = { on: true };

/** Which palette each material was washed with, so a palette change re-washes it once. */
const washed = new WeakMap<THREE.Material, string>();

const _dir = new THREE.Vector3();

/** Moves a live unit `alpha` of the way between its last two sim positions and plays its gait; returns how high it hovers. */
export function stepUnit(view: EnemyView, alpha: number, dt: number, time: number, stage: BossStage, reduced: boolean): number {
  view.root.position.lerpVectors(view.prev, view.cur, alpha);
  const dir = _dir.subVectors(view.cur, view.prev);
  const moving = dir.lengthSq() > 1e-6;
  const head = view.hq && view.stride > 0 ? roundPath(view, moving ? Math.atan2(dir.x, dir.z) : NaN, dt) : NaN;   // rounded corners
  standOnGround(view, dir, moving, dt);
  // a walker turns at a set rate, a planted one with its feet locked first (enemyTurn.ts)
  const turning = turnBody(view, moving ? (Number.isNaN(head) ? Math.atan2(dir.x, dir.z) : head) + (view.hq ? HQ_YAW : 0) : NaN, dt);
  if (moving && view.stride > 0 && view.speed < ENEMY_GAIT.standSpeed) view.rig.release();   // setting off (speed: last frame's) ends a cast or blow
  view.age += dt;
  view.speed = Math.sqrt(dir.lengthSq()) * SimConstants.TicksPerSecond;          // cells per tick → per second
  let hover = 0;
  if (view.hq) {
    walkGait(view, moving, dt);
    view.rig.update(dt);
    // the soles held where they stand while it turns, stands or comes out of a one-shot (footIk.ts does the holding)
    view.sinceShot = view.rig.busy ? 0 : view.sinceShot + dt;
    view.ik?.lock(footLock(view.stride, moving, turning, view.sinceShot, view.coarse || Math.abs(view.foot.pv) + Math.abs(view.foot.rv) > ENEMY_GAIT.lock.tiltRate));
    hover = view.air ? flyHeight(view) + Math.sin(time * 3 + view.phase) * 0.06 : 0;
    view.body.position.y = roadY() + hover;
    feel(view, dt, moving, stage, reduced);
  } else if (view.air) {
    hover = FLYER_HEIGHT;
    view.body.position.y = ROAD_Y + FLYER_HEIGHT + Math.sin(time * 3 + view.phase) * 0.08;
    view.body.rotation.z = Math.sin(time * 3 + view.phase) * 0.05;
  } else {
    const hop = Math.abs(Math.sin(time * 7 + view.phase));
    view.body.position.y = ROAD_Y + hop * 0.12;
    view.body.rotation.z = Math.sin(time * 7 + view.phase) * 0.08;
  }
  return hover;
}

/** The walk follows the ground covered (enemyGait.ts): a measured model is posed at the cycles walked, so a planted sole
 * stays put however its speed changes; an unmeasured one (a glide) keeps the time-scaled rule. */
function walkGait(view: EnemyView, moving: boolean, dt: number): void {
  const p = view.root.position;
  const dist = Math.hypot(p.x - view.lastX, p.z - view.lastZ);
  view.lastX = p.x; view.lastZ = p.z;
  if (view.stride <= 0) {
    view.rig.playLoop(moving ? "walk" : "idle", moving ? fallbackRate(view.speed) : 1, true);
    view.run = 0;
    return;
  }
  view.rig.playLoop(moving ? "walk" : "idle", moving ? 0 : 1, false);
  if (!moving) { view.run = 0; return; }
  view.walked = (view.walked + walkCycles(dist, dt, view.stride, view.body.rotation.x)) % 1;   // tilt: feel()'s pitch less lean
  view.rig.seekLoop(view.walked);
  view.run = runShare(view.speed, view.stride);
}


const _foot: Footing = { y: 0, pitch: 0, roll: 0 };
/** The ground as drawn under a point: the baked sheet, or a plank deck as it sags under the units on it. */
const groundUnder = (x: number, z: number): number => GroundMesh.drawnAt(x, z) - deckSagAt(x, z);
/**
 * The root on the ground exactly as drawn (GroundMesh.drawnAt, the baked triangles): under the footprint, a step eased
 * out and a ramp followed without lag (groundFollow.ts). A flyer's root rides the same ground; its body keeps its height.
 */
function standOnGround(view: EnemyView, dir: THREE.Vector3, moving: boolean, dt: number): void {
  const p = view.root.position, f = view.foot;
  // the footprint lies the way the body faces (an HQ body turns at its own rate, enemyGait.ts turnToward), else the way it moves
  if (view.hq) { const yaw = view.body.rotation.y - HQ_YAW; f.fx = Math.sin(yaw); f.fz = Math.cos(yaw); }
  else if (moving) { const l = Math.hypot(dir.x, dir.z); if (l > 1e-6) { f.fx = dir.x / l; f.fz = dir.z / l; } }
  footing(groundUnder, p.x, p.z, f.fx, f.fz, f, _foot);
  p.y = followGround(f, _foot.y, _foot.pitch, _foot.roll, p.x, p.z, dt);
}

const _feet = new THREE.Vector3();
const _at = new THREE.Vector3();
const _imm = new THREE.Color();
/** The immune ward's ground disc (cells) and its alpha with a breathing pulse (M36). */
export const IMMUNE_AURA = { radius: 0.42, alpha: 0.5, pulse: 0.18 } as const;

/** A unit's aura, badge plate and the status marks drawn on it, this frame. */
export function drawMarks(view: EnemyView, hover: number, shown: number, dt: number, marks: EnemyMarks, fx: { enemyStatus?: VFX["enemyStatus"] } | null, time: number, reduced: boolean): void {
  const p = view.root.position, big = view.boss ? "boss" : view.role.role === "brute" ? "big" : "unit";
  if (view.aura > 0) marks.aura(p.x, p.y + roadY() + 0.006, p.z, view.aura, view.colour, shown * AURA_ALPHA);
  // M36: an immune unit stands in a ward of its immunity's colour, breathing — readable at play zoom under the plate
  if (view.imm !== 0) {
    const kinds = immunityKinds(view.imm);
    _imm.setHex(STATUS_COLOUR[IMMUNE_STATUS[kinds[Math.floor(time * 0.8) % kinds.length]]]);
    marks.aura(p.x, p.y + roadY() + 0.007, p.z, IMMUNE_AURA.radius * (view.role.role === "brute" ? 1.3 : 1), _imm, shown * (IMMUNE_AURA.alpha + IMMUNE_AURA.pulse * Math.sin(time * 4.2 + view.phase)));
  }
  marks.plate(p.x, p.y + hover + (view.hq ? view.headNow : view.headY) + PLATE_LIFT, p.z, ROLE_CELL[view.boss ? "boss" : view.role.role], view.hp, view.s1, view.s2, view.colour, shown, BADGE_PX[big], view.boss);
  if (!reduced && fx?.enemyStatus && hasMarks(view.marks)) {
    view.marks.time = time;
    _feet.set(p.x + view.body.position.x, p.y + roadY() + hover, p.z + view.body.position.z);
    fx.enemyStatus(_feet, view.hq ? view.headNow : view.headY, view.marks, dt);
  }
}

/** A floating number over a unit's head (kill points). */
export function floatAt(marks: EnemyMarks, v: EnemyView, text: string, colour: THREE.Color, px: number): void {
  _at.copy(v.root.position); _at.y += v.headY + flyHeight(v) + PLATE_LIFT * 2;
  marks.float(text, _at, colour, px, FLOAT.life);
}

/** The fortress's loss on a leak, over the fortress. */
export function floatAtBase(marks: EnemyMarks, base: { x: number; y: number }, text: string): void {
  const b = base;
  _at.set(b.x, groundAt(b.x, b.y) + FLOAT.baseLift, b.y);
  marks.float(text, _at, FLOAT.red, FLOAT.leakPx, FLOAT.life * 1.3);
}
